// Shadows that live objects cast on the baked room.
//
// The room's atlases already hold every static shadow. What moves (devices,
// game cases) is drawn into two maps, and each baked surface darkens from
// them:
//
//   key      a depth map from the key light's direction. A surface darkens
//            where a live object stands between it and the key. How much
//            comes from the room's shade atlas: the ratio of a bake without
//            the key to the full bake. A texel the key never reaches has a
//            ratio of one, so a live shadow cannot appear on a surface the
//            bake already shows in shadow, and both kinds of shadow agree.
//
//   contact  the lowest live surface seen from straight above, blurred. An
//            upward-facing surface darkens under an object that rests on it
//            or hangs close above it. This stands in for the soft light from
//            above that the object keeps off the surface.
//
// Both maps cover the part of the room live objects can occupy, not the whole
// room, and are redrawn only when something that casts has moved.

import * as THREE from "three";
import { FullScreenQuad } from "three/addons/postprocessing/Pass.js";

/** Live objects enable this layer on every mesh that should cast. */
export const LAYER_CAST = 3;

const KEY_SIZE = 2048;
const CONTACT_SIZE = 512;
/** Standard deviation of the contact blur, in metres. */
const CONTACT_BLUR = 0.03;

const POISSON = [
  [-0.9420, -0.3991], [0.9456, -0.7689], [-0.0942, -0.9294], [0.3450, 0.2939],
  [-0.9159, 0.4577], [-0.8154, -0.8791], [-0.3828, 0.2768], [0.9748, 0.7565],
  [0.4432, -0.9751], [0.5374, -0.4737], [-0.2650, -0.4189], [0.7920, 0.1909],
  [-0.2419, 0.9971], [-0.8141, 0.9144], [0.1998, 0.7864], [0.1438, -0.1410],
];

const VERTEX_DECLARATIONS = /* glsl */ `
uniform mat4 keyMatrix;
uniform mat4 contactMatrix;
varying vec3 vKeyCoord;
varying vec3 vContactCoord;
varying vec3 vLivePosition;
`;

const VERTEX_BODY = /* glsl */ `
vec4 livePosition = modelMatrix * vec4(transformed, 1.0);
vLivePosition = livePosition.xyz;
vKeyCoord = (keyMatrix * livePosition).xyz * 0.5 + 0.5;
vContactCoord = (contactMatrix * livePosition).xyz * 0.5 + 0.5;
`;

const FRAGMENT_DECLARATIONS = /* glsl */ `
uniform sampler2D keyDepth;
uniform sampler2D contactMap;
uniform sampler2D shadeMap;
// Width and depth of the key's box in metres, tangent of the key's half angle, strength.
uniform vec4 keyShape;
// Depth of the contact box in metres, strength.
uniform vec2 contactShape;
varying vec3 vKeyCoord;
varying vec3 vContactCoord;
varying vec3 vLivePosition;
const vec2 KEY_DISK[16] = vec2[16](${POISSON.map(([x, y]) => `vec2(${x!.toFixed(4)}, ${y!.toFixed(4)})`).join(", ")});

// How much of the key a live object blocks from this fragment, 0..1. The
// filter widens with the gap between blocker and surface the way the key's
// own disc does, so a shadow is crisp where a device touches down and soft
// where it hangs in the air.
float keyOcclusion() {
  if (any(lessThan(vKeyCoord.xy, vec2(0.0))) || any(greaterThan(vKeyCoord.xy, vec2(1.0)))) return 0.0;
  float z = vKeyCoord.z - 0.002 / keyShape.y;
  float turn = 6.2831853 * fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  mat2 spin = mat2(cos(turn), -sin(turn), sin(turn), cos(turn));
  float reach = 0.04 / keyShape.x;
  float sum = 0.0;
  float hits = 0.0;
  for (int i = 0; i < 16; i++) {
    float d = texture2D(keyDepth, vKeyCoord.xy + spin * KEY_DISK[i] * reach).r;
    if (d < z) { sum += d; hits += 1.0; }
  }
  if (hits < 0.5) return 0.0;
  float gap = (z - sum / hits) * keyShape.y;
  float radius = clamp(gap * keyShape.z, 0.0025, 0.04) / keyShape.x;
  float blocked = 0.0;
  for (int i = 0; i < 16; i++) {
    blocked += step(texture2D(keyDepth, vKeyCoord.xy + spin * KEY_DISK[i] * radius).r, z);
  }
  return blocked / 16.0;
}

// How much of the light from above a live object keeps off this fragment,
// 0..1. The map holds blurred coverage and blurred coverage times depth, so
// their ratio is the depth of the live surfaces overhead.
float contactOcclusion() {
  if (any(lessThan(vContactCoord.xy, vec2(0.0))) || any(greaterThan(vContactCoord.xy, vec2(1.0)))) return 0.0;
  vec4 above = texture2D(contactMap, vContactCoord.xy);
  if (above.r < 0.004) return 0.0;
  float gap = (vContactCoord.z - above.g / above.r) * contactShape.x;
  vec3 facing = normalize(cross(dFdx(vLivePosition), dFdy(vLivePosition)));
  return above.r * (1.0 - smoothstep(0.06, 0.4, gap)) * step(-0.03, gap) * smoothstep(0.25, 0.75, facing.y);
}
`;

// The atlases are display-referred, so both factors apply to the encoded
// value. 2.2 is close enough to the sRGB curve for a multiply.
const FRAGMENT_BODY = /* glsl */ `
float keyBlocked = keyOcclusion() * keyShape.w;
float contactBlocked = contactOcclusion() * contactShape.y;
if (keyBlocked + contactBlocked > 0.0) {
  vec3 shown = pow(diffuseColor.rgb, vec3(1.0 / 2.2));
  shown *= mix(vec3(1.0), texture2D(shadeMap, vMapUv).rgb, keyBlocked) * (1.0 - contactBlocked);
  diffuseColor.rgb = pow(shown, vec3(2.2));
}
`;

const LOWEST_VERTEX = /* glsl */ `
void main() {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const LOWEST_FRAGMENT = /* glsl */ `
void main() {
  gl_FragColor = vec4(1.0, gl_FragCoord.z, 0.0, 1.0);
}
`;

const BLUR_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const BLUR_FRAGMENT = /* glsl */ `
uniform sampler2D map;
uniform vec2 stride;
uniform float sigma;
varying vec2 vUv;
void main() {
  vec4 sum = vec4(0.0);
  float total = 0.0;
  for (int i = -8; i <= 8; i++) {
    float weight = exp(-float(i * i) / (2.0 * sigma * sigma));
    sum += texture2D(map, vUv + stride * float(i)) * weight;
    total += weight;
  }
  gl_FragColor = sum / total;
}
`;

function corners(box: THREE.Box3): THREE.Vector3[] {
  const { min, max } = box;
  return [0, 1, 2, 3, 4, 5, 6, 7].map((i) => new THREE.Vector3(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z));
}

export class LiveShadows {
  /** The key light's view of the room: the light's own shadow camera copies it. */
  readonly keyCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 20);
  private contactCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 20);
  private keyTarget: THREE.WebGLRenderTarget;
  private lowest: THREE.WebGLRenderTarget;
  private blurred: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private depthOnly = new THREE.MeshBasicMaterial({ colorWrite: false, side: THREE.DoubleSide });
  // Depth keeps the fragment farthest from above, which is a live object's underside.
  private lowestSurface = new THREE.ShaderMaterial({
    vertexShader: LOWEST_VERTEX,
    fragmentShader: LOWEST_FRAGMENT,
    depthFunc: THREE.GreaterDepth,
    side: THREE.DoubleSide,
  });
  private blurUniforms = { map: { value: null as THREE.Texture | null }, stride: { value: new THREE.Vector2() }, sigma: { value: 1 } };
  private blur = new FullScreenQuad(new THREE.ShaderMaterial({
    vertexShader: BLUR_VERTEX,
    fragmentShader: BLUR_FRAGMENT,
    uniforms: this.blurUniforms,
    depthTest: false,
    depthWrite: false,
  }));
  private stale = true;
  /** Uniforms shared by every room material. */
  readonly uniforms = {
    keyMatrix: { value: new THREE.Matrix4() },
    keyDepth: { value: null as THREE.Texture | null },
    keyShape: { value: new THREE.Vector4(1, 1, 0.06, 0.96) },
    contactMatrix: { value: new THREE.Matrix4() },
    contactMap: { value: null as THREE.Texture | null },
    contactShape: { value: new THREE.Vector2(1, 0.34) },
  };

  constructor() {
    const depth = new THREE.DepthTexture(KEY_SIZE, KEY_SIZE);
    depth.type = THREE.UnsignedIntType;
    depth.minFilter = THREE.NearestFilter;
    depth.magFilter = THREE.NearestFilter;
    this.keyTarget = new THREE.WebGLRenderTarget(KEY_SIZE, KEY_SIZE, { depthTexture: depth, depthBuffer: true });
    this.uniforms.keyDepth.value = depth;
    const soft = { type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter };
    this.lowest = new THREE.WebGLRenderTarget(CONTACT_SIZE, CONTACT_SIZE, { ...soft, depthBuffer: true });
    this.blurred = [
      new THREE.WebGLRenderTarget(CONTACT_SIZE, CONTACT_SIZE, { ...soft, depthBuffer: false }),
      new THREE.WebGLRenderTarget(CONTACT_SIZE, CONTACT_SIZE, { ...soft, depthBuffer: false }),
    ];
    this.uniforms.contactMap.value = this.blurred[1].texture;
    this.keyCamera.layers.set(LAYER_CAST);
    this.contactCamera.layers.set(LAYER_CAST);
  }

  /**
   * Aim the key map along `direction`. `reach` is the box live objects stay
   * inside: both maps span it, which is all a shadow needs, since a surface
   * a live object shades lies behind that object along the light. `bounds`
   * is the whole room: the key box is that deep, so the room can shade the
   * live objects through the same box. `angle` is the angular diameter of
   * the key in radians: it sets how fast a key shadow softens with distance.
   */
  fit(bounds: THREE.Box3, reach: THREE.Box3, direction: THREE.Vector3, angle: number): void {
    const centre = reach.getCenter(new THREE.Vector3());
    const key = this.keyCamera;
    key.position.copy(centre);
    key.up.set(0, 1, 0);
    key.lookAt(centre.clone().add(direction));
    key.updateMatrixWorld(true);
    let half = 0;
    for (const corner of corners(reach)) {
      corner.applyMatrix4(key.matrixWorldInverse);
      half = Math.max(half, Math.abs(corner.x), Math.abs(corner.y));
    }
    // Depth along the light: a camera looks down its own -Z.
    const depths = corners(bounds).map((corner) => -corner.applyMatrix4(key.matrixWorldInverse).z);
    const first = Math.min(...depths);
    key.position.addScaledVector(direction, first - 0.1);
    key.left = key.bottom = -half;
    key.right = key.top = half;
    key.near = 0.05;
    key.far = Math.max(...depths) - first + 0.2;
    key.updateProjectionMatrix();
    key.updateMatrixWorld(true);
    this.uniforms.keyMatrix.value.multiplyMatrices(key.projectionMatrix, key.matrixWorldInverse);
    this.uniforms.keyShape.value.set(half * 2, key.far - key.near, Math.tan(angle / 2), this.uniforms.keyShape.value.w);

    // Contact: the same box from straight above, with room for the blur at
    // its edges and for the surfaces a little below the lowest live object.
    const size = reach.getSize(new THREE.Vector3());
    const span = Math.max(size.x, size.z) + CONTACT_BLUR * 8;
    const contact = this.contactCamera;
    contact.position.set(centre.x, reach.max.y + 0.1, centre.z);
    contact.up.set(0, 0, -1);
    contact.lookAt(centre.x, reach.min.y, centre.z);
    contact.left = contact.bottom = -span / 2;
    contact.right = contact.top = span / 2;
    contact.near = 0.05;
    contact.far = size.y + 0.7;
    contact.updateProjectionMatrix();
    contact.updateMatrixWorld(true);
    this.uniforms.contactMatrix.value.multiplyMatrices(contact.projectionMatrix, contact.matrixWorldInverse);
    this.uniforms.contactShape.value.x = contact.far - contact.near;
    this.blurUniforms.sigma.value = (CONTACT_BLUR / span) * CONTACT_SIZE;
    this.stale = true;
  }

  /** Something that casts has moved: redraw the maps before the next frame. */
  invalidate(): void {
    this.stale = true;
  }

  /** Redraw the maps if they are stale; true when they were. Call before rendering the room. */
  update(renderer: THREE.WebGLRenderer, scene: THREE.Scene): boolean {
    if (!this.stale) return false;
    this.stale = false;
    const before = {
      target: renderer.getRenderTarget(),
      override: scene.overrideMaterial,
      colour: renderer.getClearColor(new THREE.Color()),
      alpha: renderer.getClearAlpha(),
    };
    scene.overrideMaterial = this.depthOnly;
    renderer.setRenderTarget(this.keyTarget);
    renderer.render(scene, this.keyCamera);

    scene.overrideMaterial = this.lowestSurface;
    renderer.setRenderTarget(this.lowest);
    renderer.setClearColor(0x000000, 0);
    renderer.state.buffers.depth.setClear(0);
    renderer.render(scene, this.contactCamera);
    renderer.state.buffers.depth.setClear(1);
    scene.overrideMaterial = before.override;

    const pass = this.blurUniforms;
    pass.map.value = this.lowest.texture;
    pass.stride.value.set(1 / CONTACT_SIZE, 0);
    renderer.setRenderTarget(this.blurred[0]);
    this.blur.render(renderer);
    pass.map.value = this.blurred[0].texture;
    pass.stride.value.set(0, 1 / CONTACT_SIZE);
    renderer.setRenderTarget(this.blurred[1]);
    this.blur.render(renderer);

    renderer.setRenderTarget(before.target);
    renderer.setClearColor(before.colour, before.alpha);
    return true;
  }

  /** Make a baked material take live shadows, with `shade` as its shade atlas. */
  receive(material: THREE.MeshBasicMaterial, shade: THREE.Texture): void {
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.uniforms, { shadeMap: { value: shade } });
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", `#include <common>\n${VERTEX_DECLARATIONS}`)
        .replace("#include <begin_vertex>", `#include <begin_vertex>\n${VERTEX_BODY}`);
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", `#include <common>\n${FRAGMENT_DECLARATIONS}`)
        .replace("#include <map_fragment>", `#include <map_fragment>\n${FRAGMENT_BODY}`);
    };
    // The shade atlas is a uniform, so every room material shares one program.
    material.customProgramCacheKey = () => "pocket-studio-live-shadows";
  }

  dispose(): void {
    this.keyTarget.dispose();
    this.lowest.dispose();
    for (const target of this.blurred) target.dispose();
    this.depthOnly.dispose();
    this.lowestSurface.dispose();
    this.blur.material.dispose();
    this.blur.dispose();
  }
}
